import React, { useState, useEffect, useContext, useRef } from "react";
import styles from "./Manage.module.css";
import ToggleSwitch from "common-ui-components/components/toggle-switch/ToggleSwitch";
import Select from "common-ui-components/components/select/Select";
import { notify } from "common-ui-components/components/notification/Notification";
import Failure from "common-ui-components/components/notification/templates/Failure";
import info from "common-ui-components/info-icon/info.svg";
import Button from "common-ui-components/components/button/Button";
import Modal from "common-ui-components/components/modal/Modal";
import CancelSubscription from "./CancelSubscription";
import EM_API from "common-ui-components/components/utils/API";
import Success from "common-ui-components/components/notification/templates/Success";
import BackButton from "common-ui-components/components/back-button/BackButton";
import { UserDetailsContext } from "App";
import { Downgrade } from "./Trailended";
import { useNavigate } from 'react-router-dom';
import { redirectToPage } from "app/common/redirects/Redirects";
import ResponseLoader from "common-ui-components/components/ResponseLoader/ResponseLoader";
import SkeletonLoader from "common-ui-components/components/ResponseLoader/Skeleton loading/SkeletonLoader";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";

const manageKey = "SUBSCRIPTIONS_MANAGE";

function ManageContent({ modal, setOpen, history }) {
  const userDetails = useContext(UserDetailsContext);

  const currentProduct = window.location.pathname.split("/")[1];
  let plan = ["Standard Plan", "Professional Plan"];

  if(currentProduct==="em" || currentProduct==="em-msp")
    plan.push("Enterprise Plan");

  const editionToPlan = {
    Standard: plan[0],
    Professional: plan[1],
    Enterprise: plan[2],
  };
  const defaultEndpointOptions = [50, 100, 250, 500, 750, 1000];
  const raEndPointOptions = [25, 50, 100, 250, 500, 750, 1000];

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [nextRenewalDate, setNextRenewalDate] = useState("");
  const [renewalAmount, setRenewalAmount] = useState(0);
  const [planDetails, setPlanDetails] = useState(plan[0]);
  const [autoRenew, setAutoRenew] = useState(null);

  const [showTechInfo, setShowTechInfo] = useState(false);
  const [endpointOptions, setEndpointOptions] = useState(() =>
    currentProduct === "ra" ? raEndPointOptions : defaultEndpointOptions,
  );
  const [selectedEndpoint, setSelectedEndpoint] = useState(-1);
  const [technicianCount, setTechnicianCount] = useState(-1);
  const [isYearly, setIsYearly] = useState(false);
  const [allPlanCombinations, setAllPlanCombinations] = useState(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [renewalDate, setRenewalDate] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downgrade, setDowngrade] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);

  const { checkRestriction } = useRestrictionChecker();
  const canManage = !checkRestriction(manageKey, false, "page");

  const lastParamsRef = useRef({ key: "" });
  const isInitialRender = useRef(true);

  // Constants for skeleton styles to ensure alignment
  const priceSkeletonStyle = {
    width: "80px",
    height: "16px",
    display: "block",
    borderRadius: "4px",
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await EM_API.get("/payment/current-plan");
        if (response?.status?.code === 200) {
          const data = response.body;
          setSubscriptionData(data);
          setIsYearly(data.billingCycle === "Annually");
          setPlanDetails(editionToPlan[data.editionName] ?? plan[0]);
          const newEndpoint = data.purchasedEndpoint;
          if (
            !Number.isNaN(newEndpoint) &&
            !defaultEndpointOptions.includes(newEndpoint)
          ) {
            setEndpointOptions((prev) =>
              [...prev, newEndpoint].sort((a, b) => a - b),
            );
          }
          setSelectedEndpoint(newEndpoint);
          setTechnicianCount(data.purchasedTechnician);
        } else {
          setSubscriptionData(null);
          setIsYearly(false);
          setPlanDetails(plan[0]);
          setSelectedEndpoint(
            currentProduct !== "ra" ? 50 : defaultEndpointOptions[0],
          );
          setTechnicianCount(0);
        }
      } catch (error) {
        console.error("Error fetching subscription data:", error);
        notify(<Failure>Error fetching subscription data.</Failure>);
      } finally {
        setIsLoading(false);
      }
    };
    if (canManage) fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      if (selectedEndpoint === -1 && technicianCount === -1) {
        setPlanLoading(true);
        try {
          const plansResp = await EM_API.post(
            `/payment/manage/plans?deviceCount=-1&technicianCount=-1&currencyCode=`,
          );

          if (plansResp.status.code === 200) {
            const planData = plansResp.body;
            setAllPlanCombinations(planData?.managePlanList);
            setNextRenewalDate(planData?.nextRenewalDate);
            setRenewalAmount(planData?.renewalAmount);
            setAutoRenew(planData?.autoRenewal);
            const newDate = isYearly
              ? planData?.nextYearlyRenewalDate
              : planData?.nextMonthlyRenewalDate;
            setRenewalDate(newDate);
          } else {
            notify(<Failure>Failed to fetch plan prices.</Failure>);
          }
        } catch (error) {
          console.error("Error fetching plans:", error);
          notify(<Failure>Error fetching plan combinations.</Failure>);
        } finally {
          setPlanLoading(false);
        }
      }
    };

    if (isInitialRender.current) {
      isInitialRender.current = false;
      if (canManage) fetchPlans();
    }
  }, []);

  useEffect(() => {
    if (
      isInitialRender.current ||
      selectedEndpoint === -1 ||
      technicianCount === -1
    ) {
      return;
    }

    const fetchPlans = async () => {
      const paramsKey = `${selectedEndpoint}-${technicianCount}-${isYearly}`;
      if (lastParamsRef.current.key === paramsKey) return;

      setPlanLoading(true);
      lastParamsRef.current.key = paramsKey;

      if (subscriptionData && selectedEndpoint < subscriptionData.deviceCount) {
        setDowngrade(true);
      } else {
        setDowngrade(false);
      }

      try {
        const plansResp = await EM_API.post(
          `/payment/manage/plans?deviceCount=${selectedEndpoint}&technicianCount=${technicianCount}&currencyCode=`,
        );

        if (plansResp.status.code === 200) {
          const planData = plansResp.body;
          setAllPlanCombinations(planData?.managePlanList);
          setNextRenewalDate(planData?.nextRenewalDate);
          setRenewalAmount(planData?.renewalAmount);
          setAutoRenew(planData?.autoRenewal);
          const newDate = isYearly
            ? planData?.nextYearlyRenewalDate
            : planData?.nextMonthlyRenewalDate;
          setRenewalDate(newDate);
        } else {
          notify(<Failure>Failed to fetch plan prices.</Failure>);
        }
      } catch (error) {
        console.error("Error fetching plans:", error);
        notify(<Failure>Error fetching plan combinations.</Failure>);
      } finally {
        setPlanLoading(false);
      }
    };
    if (canManage) fetchPlans();
  }, [selectedEndpoint, technicianCount, isYearly, subscriptionData, planDetails]);

  const formatPrice = (amount, currencyCode) => {
    if (amount === null || amount === undefined || !currencyCode) return "";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const handlePlanSelect = (selectedOption, selectedIndex) => {
    if (typeof selectedOption === "string") {
      setPlanDetails(selectedOption);
      return;
    }

    if (
      typeof selectedOption === "number" &&
      selectedOption >= 0 &&
      selectedOption < plan.length
    ) {
      setPlanDetails(plan[selectedOption]);
      return;
    }

    if (
      typeof selectedIndex === "number" &&
      selectedIndex >= 0 &&
      selectedIndex < plan.length
    ) {
      setPlanDetails(plan[selectedIndex]);
    }
  };

  const handleUpdate = async () => {
    if (checkRestriction(manageKey)) return;
    const update = await EM_API.get("/payment/update-details");
    if (update.status.success && update.body) {
      const cbInstance = window.Chargebee.getInstance();
      cbInstance.openCheckout({
        hostedPage: () => Promise.resolve(update.body),
      });
    }
  };

  const EndpointSelectContent = ({
    open,
    setOpen,
    options,
    onOptionSelect,
    active,
    addNew,
  }) => {
    const [value, setValue] = useState("");
    const [adding, setAdding] = useState(false);

    const handleAddBtnClick = (e) => {
      e.preventDefault();
      if (!adding) {
        setAdding(true);
        return;
      }

      const num = parseInt(value, 10);
      if (isNaN(num)) return;
      if (value.toLowerCase().includes("e")) {
        notify(<Failure>Scientific notation is not allowed.</Failure>);
        return;
      }
      if (num < 50) {
        notify(<Failure>The minimum endpoints is 50.</Failure>);
        return;
      }
      if (num > 10000) {
        notify(
          <Failure>Contact support for plans over 10,000 endpoints.</Failure>,
        );
        return;
      }
      addNew(num);
      setOpen(false);
      setAdding(false);
      setValue("");
    };
    return (
      <div className={styles.endpointSelectContent}>
        <ul className={styles.optionsList}>
          {options.map((option, index) => (
            <li
              key={index}
              className={`${styles.optionItem} ${
                active === index ? styles.active : ""
              }`}
              onClick={() => {
                onOptionSelect(option);
                setOpen(false);
              }}
            >
              {option}
            </li>
          ))}
        </ul>
        {/* {currentProduct !== "ra" && ( */}
        {(
          <div className={styles.addEndpoint}>
            {adding && (
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Endpoints"
                className={styles.customInput}
              />
            )}
            <button onClick={handleAddBtnClick} className={styles.addBtn}>
              {adding ? "Add" : "Add Endpoints"}
            </button>
          </div>
        )}
      </div>
    );
  };

  const normalizedPlanName = planDetails.replace(" Plan", "").toLowerCase();
  const normalizedPeriod = isYearly ? "annually" : "monthly";

  const planCandidates =
    allPlanCombinations?.filter(
      (planItem) =>
        planItem.planName?.toLowerCase() === normalizedPlanName &&
        planItem.period?.toLowerCase() === normalizedPeriod,
    ) || [];

  // Prefer exact match, then fallback by device count, then first plan for selected plan/period.
  // Some API responses may normalize technician count (for example 0), so strict matching can hide pricing.
  const currentPlanDetails =
    planCandidates.find(
      (planItem) =>
        planItem.deviceCount === selectedEndpoint &&
        planItem.technicianCount === technicianCount,
    ) ||
    planCandidates.find((planItem) => planItem.deviceCount === selectedEndpoint) ||
    planCandidates[0];

  const handleUpdateSubscription = async () => {
    if (checkRestriction(manageKey)) return;
    if (!currentPlanDetails) {
      notify(
        <Failure>Please select valid plan details before updating.</Failure>,
      );
      return;
    }

    setLoadingUpdate(true);
    try {
      const payload = {
        planId: currentPlanDetails.planId,
        deviceCount: currentPlanDetails.deviceCount,
        technicianCount: currentPlanDetails.technicianCount,
        currencyCode: "",
      };

      const response = await EM_API.post("/payment/create-checkout", payload);

      if (response.status.success && response.body) {
        if (modal && setOpen) {
          setOpen(false);
        }
        if (window.Chargebee && window.Chargebee.getInstance) {
          const cbInstance = window.Chargebee.getInstance();
          cbInstance.openCheckout({
            hostedPage: () => Promise.resolve(response.body),
          });
        } else {
          notify(<Failure>Chargebee SDK not loaded.</Failure>);
        }
      } else {
        notify(
          <Failure>
            {response.status.message || "Failed to create checkout session."}
          </Failure>,
        );
      }
    } catch (err) {
      console.error("Checkout creation failed:", err);
      notify(<Failure>An error occurred while updating subscription.</Failure>);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const clickBack = () => {
    redirectToPage(history, "/app/settings/account/subscription");
  };

  if (isLoading) return <ResponseLoader simple={true} />;

  return (
    <div
      style={modal ? { height: "fit-content" } : {}}
      className={[styles.container, "scrollBarOnHover"].join(" ")}
    >
      <div className={styles.subParent}>
        <div style={{ background: "#FFFFFF" }} className={styles.header}>
          {modal ? null : (
            <section className={styles.backBtn}>
              <BackButton onClick={clickBack} /> Back
            </section>
          )}
          <h3 className={styles.title}>Manage Subscription</h3>
          <p className={styles.renewal}>Renews On {renewalDate}</p>
        </div>

        <main className={styles.planDetails}>
          <div className={styles.header}>
            <div style={{ padding: "0.2rem 0.8rem" }}>
              <div className={styles.planDuration}>
                <p
                  style={
                    isYearly
                      ? { fontStyle: "normal", fontWeight: "var(--font-regular)" }
                      : {
                          color: "#0B8E18",
                          fontStyle: "bold",
                          fontWeight: "700",
                        }
                  }
                >
                  Monthly
                </p>
                <ToggleSwitch
                  size="2rem"
                  checked={isYearly}
                  onCheckChange={() => setIsYearly(!isYearly)}
                />
                <p
                  style={
                    !isYearly
                      ? { fontStyle: "normal", fontWeight: "var(--font-regular)" }
                      : {
                          color: "#0B8E18",
                          fontStyle: "bold",
                          fontWeight: "700",
                        }
                  }
                >
                  Yearly
                </p>
              </div>
            </div>
            <Select
              options={plan}
              active={plan.indexOf(planDetails)}
              onOptionSelect={handlePlanSelect}
              triggerProps={{ className: styles.select }}
            />
          </div>
          <section className={styles["flex-container"]}>
            <div className={styles["flex-header"]}>
              <div className={styles["flex-cell-item"]}>Items</div>
              <div className={styles["flex-cell-unit"]}>Units</div>
              <div className={styles["flex-cell-price"]}>
                Total/{isYearly ? "Year" : "Month"}
              </div>
            </div>
            <div className={styles["flex-body"]}>
              <div
                style={{ borderBottom: "none" }}
                className={styles["flex-row"]}
              >
                <div className={styles["flex-cell-item"]}>Endpoints</div>
                <div className={styles["flex-cell-unit"]}>
                  <Select
                    options={endpointOptions}
                    active={endpointOptions.indexOf(selectedEndpoint)}
                    onOptionSelect={setSelectedEndpoint}
                    triggerProps={{ className: styles.selectDrop }}
                    childrenProps={{
                      addNew: (newVal) => {
                        if (!endpointOptions.includes(newVal)) {
                          const updated = [...endpointOptions, newVal].sort(
                            (a, b) => a - b,
                          );
                          setEndpointOptions(updated);
                          setSelectedEndpoint(newVal);
                        }
                      },
                    }}
                  >
                    {EndpointSelectContent}
                  </Select>
                </div>
                <div className={styles["flex-cell-price"]}>
                  {planLoading ? (
                    <SkeletonLoader style={priceSkeletonStyle} />
                  ) : (
                    formatPrice(
                      currentPlanDetails?.deviceTotalPrice,
                      currentPlanDetails?.currencyCode,
                    )
                  )}
                </div>
              </div>
              {/* {currentProduct !== "ra" && ( */}
              {(
                <div className={styles["flex-row"]}>
                  <div className={styles["flex-cell-item"]}>
                    Additional Technician
                    <div
                      className={styles.infoWrapper}
                      onMouseEnter={() => setShowTechInfo(true)}
                      onMouseLeave={() => setShowTechInfo(false)}
                    >
                      <img
                        src={info}
                        alt="info icon"
                        className={styles.infoIcon}
                      />
                      {showTechInfo && (
                        <div className={styles.infoTooltip}>
                          All plans include one technician license by default.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles["flex-cell-unit"]}>
                    <Select
                      options={[...Array(51).keys()]}
                      active={technicianCount}
                      onOptionSelect={setTechnicianCount}
                      triggerProps={{ className: styles.selectDrop }}
                    />
                  </div>
                  <div className={styles["flex-cell-price"]}>
                    {planLoading ? (
                      <SkeletonLoader style={priceSkeletonStyle} />
                    ) : (
                      formatPrice(
                        currentPlanDetails?.technicianTotalPrice,
                        currentPlanDetails?.currencyCode,
                      )
                    )}
                  </div>
                </div>
              )}
              {isYearly && (
                <>
                  <div className={styles["flex-row-separator"]}></div>

                  <div className={styles["flex-row-final"]}>
                    <div className={styles["flex-cell-item-final"]} />

                    <div className={styles["flex-cell-unit-final"]}>
                      <div className={styles.discountLabel}>
                        {planLoading ? (
                          <SkeletonLoader
                            style={{
                              width: "100px",
                              height: "15px",
                              display: "inline-block",
                            }}
                          />
                        ) : currentPlanDetails?.discountPercentage ? (
                          `Discount ${currentPlanDetails.discountPercentage.toFixed(2)}%`
                        ) : (
                          <span
                            style={{
                              // visibility: "hidden",
                              color: "#F24E1E",
                            }}
                          >
                            Discount 00%
                          </span>
                        )}
                      </div>
                    </div>

                    <div className={styles["flex-cell-price-final"]}>
                      <div className={styles.priceWrapper}>
                        {planLoading ? (
                          <SkeletonLoader style={priceSkeletonStyle} />
                        ) : currentPlanDetails?.totalPrice ? (
                          <span style={{ textDecoration: "line-through" }}>
                            {formatPrice(
                              currentPlanDetails?.totalPrice,
                              currentPlanDetails?.currencyCode,
                            )}
                          </span>
                        ) : (
                          <span style={{ visibility: "hidden" }}>₹0.00</span>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className={styles["flex-row-final"]}>
                <div className={styles["flex-cell-item-final"]}>
                  Final amount
                </div>
                <div className={styles["flex-cell-unit-final"]}>
                  {isYearly && (
                    <div className={styles.finalSavedAmount}>
                      {planLoading ? (
                        <SkeletonLoader
                          style={{
                            width: "120px",
                            height: "15px",
                            display: "inline-block",
                          }}
                        />
                      ) : currentPlanDetails?.savedAmount ? (
                        <>
                          You saved{" "}
                          {formatPrice(
                            currentPlanDetails?.savedAmount,
                            currentPlanDetails?.currencyCode,
                          )}
                        </>
                      ) : (
                        <span style={{ visibility: "hidden" }}>
                          You saved ₹0.00
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles["flex-cell-price-final"]}>
                  {planLoading ? (
                    <SkeletonLoader style={priceSkeletonStyle} />
                  ) : (
                    formatPrice(
                      currentPlanDetails?.discountedPrice ||
                        currentPlanDetails?.totalPrice,
                      currentPlanDetails?.currencyCode,
                    )
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Modal open={downgrade} setOpen={setDowngrade}>
          <Downgrade />
        </Modal>

        <div className={styles.updateBtn}>
          <Button
            children={loadingUpdate ? "Updating..." : "Update"}
            variant={"primary"}
            onClick={handleUpdateSubscription}
            disabled={loadingUpdate || !currentPlanDetails || planLoading}
          />
        </div>

        {!modal && (
          <>
            {/* <section className={styles.autoUpgrade}>
              <div className={styles.enablePay}>
                <p className={styles.heading}>Enable Auto upgrade</p>
                <ToggleSwitch
                  size={"2rem"}
                  checked={autoRenew}
                  onCheckChange={async () => {
                    const newAutoRenewState = !autoRenew;
                    setAutoRenew(newAutoRenewState);
                    try {
                      const response = await EM_API.put(
                        `/payment/auto-renew?autoRenew=${newAutoRenewState}`,
                      );
                      if (response.status) {
                        notify(
                          <Success>
                            Auto-renewal settings updated successfully.
                          </Success>,
                        );
                      } else {
                        setAutoRenew(!newAutoRenewState);
                        notify(
                          <Failure>
                            {response.status.message ||
                              "Failed to update auto-renewal."}
                          </Failure>,
                        );
                      }
                    } catch (error) {
                      console.error("Error updating auto-renewal:", error);
                      setAutoRenew(!newAutoRenewState);
                      notify(
                        <Failure>
                          An error occurred while updating auto-renewal
                          settings.
                        </Failure>,
                      );
                    }
                  }}
                />
              </div>
              <p>
                On checking this option, will auto-renew your subscription if it
                expires.
              </p>
            </section> */}

            <section className={styles.paymentDetails}>
              <div className={styles.payCard}>
                <p className={styles.heading}>Payment detail</p>
                <p>
                  Next charge:{" "}
                  {planLoading ? (
                    <SkeletonLoader
                      style={{
                        width: "100px",
                        height: "15px",
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                    />
                  ) : (
                    formatPrice(renewalAmount, currentPlanDetails?.currencyCode)
                  )}{" "}
                  on {planLoading ? "..." : nextRenewalDate}
                </p>
                <p className={styles.updateTxt} onClick={handleUpdate}>
                  Update payment method
                </p>
              </div>
              <div className={styles.payCard}>
                <p className={styles.heading}>Subscription Settings</p>
                <p>
                  You are on {userDetails?.plan} Plan and have{" "}
                  {userDetails.daysLeft} days remaining.
                </p>
                <p
                  onClick={() => setCancelModalOpen(true)}
                  className={styles.cancelTxt}
                >
                  Cancel Subscription
                </p>
                <Modal open={cancelModalOpen} setOpen={setCancelModalOpen}>
                  <div style={{ width: "40vw", borderRadius: "0.25rem" }}>
                    {cancelModalOpen && (
                      <CancelSubscription
                        dueDate={nextRenewalDate}
                        onClose={() => setCancelModalOpen(false)}
                      />
                    )}
                  </div>
                </Modal>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Manage(props) {
  if (props.modal) {
    return <ManageContent {...props} history={null} />;
  }

  return <ManageWithNavigation {...props} />;
}

function ManageWithNavigation(props) {
  const history = useNavigate();
  return <ManageContent {...props} history={history} />;
}

export default Manage;